// ---------------------------------------------------------------------------
// The URL grammar — PURE. No imports, no DOM, no store reads, no localStorage.
// ---------------------------------------------------------------------------
// Exactly four canonical strings:
//
//   #/                              Home (dashboard)
//   #/p/<areaId>/<view>             project + view (board|table|calendar|history)
//   #/t/<taskId>                    task open, Home behind it
//   #/p/<areaId>/<view>/t/<taskId>  task open, that project+view behind it
//
// SEMANTICS: the `p/` segment describes WHAT IS BEHIND the task panel
// (selectedAreaId) — NOT the task's own project. So `#/t/345` means "task 345
// with Home behind it", and the router never upgrades it to `#/p/<id>/…` from the
// loaded task: a non-member assignee has task-level access only (v1.14.1), and a
// project segment they can't open would be a broken link they then re-share.
//
// LEGACY `#t/<id>` IS PERMANENT. Staff have pasted those links into Odoo chatter
// and chats, and the server writes them into exported CSVs. They normalize to
// `#/t/<id>` with replaceState (no history entry) and must keep working forever.
// ---------------------------------------------------------------------------

export type RouteView = 'board' | 'table' | 'calendar' | 'history';
const VIEWS: readonly string[] = ['board', 'table', 'calendar', 'history'];

export interface Route {
  areaId: number | null;
  /** null only when areaId is null (Home). */
  view: RouteView | null;
  taskId: number | null;
}

export const HOME: Route = { areaId: null, view: null, taskId: null };

/** Ids must be small positive integers — rejects 0, negatives, overflow, "abc". */
function id(raw: string | undefined): number | null {
  if (!raw || !/^\d{1,9}$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * Parse a location.hash into a Route.
 *
 * `canonical: false` means the URL must be rewritten (always replaceState, never
 * push) — junk, a legacy `#t/` link, a bad view, or a missing view.
 */
export function parse(raw: string): { route: Route; canonical: boolean } {
  let h = raw || '';
  if (h.startsWith('#')) h = h.slice(1);
  // Chat clients glue punctuation onto pasted links ("see #t/345." / "(#t/345)"),
  // and trackers append ?utm=…  Strip both before tokenizing.
  h = h.replace(/[?&].*$/, '').replace(/[).,;:!?'"»>\s\]]+$/, '');

  // Legacy: `#t/<id>` (and a tolerated `#/t/<id>` written the old way).
  const legacy = /^\/?t\/(\d{1,9})$/.exec(h);
  if (legacy) {
    const t = id(legacy[1]);
    if (t === null) return { route: HOME, canonical: false };
    // Canonical only if it was already written as `/t/<id>`.
    return { route: { areaId: null, view: null, taskId: t }, canonical: h.startsWith('/t/') };
  }

  if (h === '' || h === '/') return { route: HOME, canonical: h === '/' };

  const parts = h.replace(/^\//, '').split('/');
  if (parts[0] !== 'p') return { route: HOME, canonical: false };

  const areaId = id(parts[1]);
  if (areaId === null) return { route: HOME, canonical: false };

  const rawView = parts[2];
  // `#/p/12` — no view. Apply it, then let the reconciler write the canonical
  // hash once the remembered view (or 'board') is known.
  if (rawView === undefined) return { route: { areaId, view: null, taskId: null }, canonical: false };
  const view = (VIEWS.includes(rawView) ? rawView : 'board') as RouteView;
  const viewOk = VIEWS.includes(rawView);

  if (parts[3] === undefined) return { route: { areaId, view, taskId: null }, canonical: viewOk };
  if (parts[3] !== 't') return { route: { areaId, view, taskId: null }, canonical: false };

  const taskId = id(parts[4]);
  if (taskId === null) return { route: { areaId, view, taskId: null }, canonical: false };
  // A trailing surplus segment is not canonical.
  return { route: { areaId, view, taskId }, canonical: viewOk && parts[5] === undefined };
}

/** Route → the canonical hash. serialize(parse(serialize(r))) === serialize(r). */
export function serialize(r: Route): string {
  const t = r.taskId !== null ? `/t/${r.taskId}` : '';
  if (r.areaId === null) return r.taskId !== null ? `#/t/${r.taskId}` : '#/';
  return `#/p/${r.areaId}/${r.view ?? 'board'}${t}`;
}

/** The ONE place a shareable task link is built (bare + project-less on purpose). */
export function taskLink(taskId: number): string {
  return `${window.location.origin}${window.location.pathname}#/t/${taskId}`;
}
